import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {ImageSource, type ImageInput} from "@khan/guide-types";

import {ImageAttachModal} from "./image-attach-modal.tsx";
import {CameraCaptureModalContent} from "./modal-content/camera-capture-modal-content.tsx";
import {FileUploadModalContent} from "./modal-content/file-upload-modal-content.tsx";
import {UrlModalContent} from "./modal-content/url-modal-content.tsx";

interface Props {
    // Function to call when the modal should be closed.
    onCloseModal: () => void;
    // Optional callback invoked with the attached images.
    onAttachImages?: (images: ImageInput[]) => void;
    // Which image input experience to show (camera, file, or URL).
    imageSource?: ImageSource;
}

/**
 * Manages the state and logic for the image attachment modal experience.
 * It determines which image input experience to show (camera, file upload, or URL),
 * manages all intermediate state, and passes the correct props to the presentational
 * ImageAttachModal component.
 */
export const ImageAttachModalWrapper = ({
    onCloseModal,
    onAttachImages,
    imageSource = ImageSource.ProvidedByFile,
}: Props) => {
    const imageInputMenuIsOn = useFeatureIsOn("image-input-menu");
    const [overTarget, setOverTarget] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [image, setImage] = useState<ImageInput>();
    const [imageURL, setImageURL] = useState<string>("");
    const [pendingImageURL, setPendingImageURL] = useState<string>("");
    const [selectedFilename, setSelectedFilename] = useState<string | null>(
        null,
    );
    const [draggedFile, setDraggedFile] = useState<File | undefined>(undefined);

    const clearImage = () => {
        setImage(undefined);
        setSelectedFilename(null);
    };

    const clearImageURL = () => {
        setImageURL("");
        setPendingImageURL("");
    };

    const handleSetImage = (dataURL: string, source: ImageInput["source"]) => {
        setImage({
            imageURL: dataURL,
            moderated: false,
            source,
        });
    };

    const handleSetImageURL = () => {
        setError(null);
        clearImage();
        handleConvertImageURLToDataURL(pendingImageURL);
    };

    const handleConvertImageURLToDataURL = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onerror = (error) => {
                handleClearImage();
            };
            reader.onload = () => {
                const maybeImageResult = reader.result as string;
                setImageURL(url);
                handleSetImage(maybeImageResult, ImageSource.ProvidedByURL);
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            setError(
                t`We couldn't fetch that image URL.  Please try again, or try a different URL`,
            );
            handleClearImage();
        }
    };

    const handleConfirmAttachment = () => {
        if (!image) {
            throw new Error("No image to attach");
        }
        onAttachImages?.([image]);
        onCloseModal();
    };

    // Removes the selected image so the user can try a different image
    const handleClearImage = () => {
        clearImage();
        clearImageURL();
        setDraggedFile(undefined);
    };

    const handleCancel = () => {
        onCloseModal();
    };

    let content: React.ReactNode = null;
    switch (imageSource) {
        case ImageSource.ProvidedByCamera:
            content = (
                <CameraCaptureModalContent image={image} setImage={setImage} />
            );
            break;
        case ImageSource.ProvidedByURL:
            content = (
                <UrlModalContent
                    image={image}
                    imageURL={imageURL}
                    error={error}
                    pendingImageURL={pendingImageURL}
                    setPendingImageURL={setPendingImageURL}
                    handleSetImageURL={handleSetImageURL}
                    handleClearImage={handleClearImage}
                    selectedFilename={selectedFilename}
                />
            );
            break;
        case ImageSource.ProvidedByFile:
        default:
            content = (
                <FileUploadModalContent
                    image={image}
                    overTarget={overTarget}
                    setOverTarget={setOverTarget}
                    handleSetImage={handleSetImage}
                    handleClearImage={handleClearImage}
                    selectedFilename={selectedFilename}
                    setSelectedFilename={setSelectedFilename}
                    imageURL={imageURL}
                    draggedFile={draggedFile}
                    setDraggedFile={setDraggedFile}
                />
            );
            break;
    }

    // TODO: (KORE-1313) Remove this when the image input menu is fully launched.
    const fileTitle = imageInputMenuIsOn
        ? t`Attach image from file`
        : t`Attach Image File`;

    const title =
        imageSource === ImageSource.ProvidedByCamera
            ? t`Attach image from camera`
            : imageSource === ImageSource.ProvidedByFile
              ? fileTitle
              : t`Attach image from URL`;

    const footer = (
        <View style={styles.footer}>
            <Button
                kind="tertiary"
                onClick={handleCancel}
                aria-label={t`Cancel image attach`}
            >
                {t`Cancel`}
            </Button>
            <Button
                kind="primary"
                disabled={!image}
                onClick={handleConfirmAttachment}
                testId="modal-attach-image-button"
            >
                {t`Attach image`}
            </Button>
        </View>
    );

    const warningMessage =
        imageSource === ImageSource.ProvidedByURL
            ? t`Please avoid linking to images that contain personal or sensitive information.`
            : imageSource === ImageSource.ProvidedByCamera
              ? t`When capturing images, ensure they don't include any sensitive information.`
              : t`Please avoid attaching images that contain personal or sensitive information.`;

    return (
        <ImageAttachModal
            title={title}
            content={
                <View style={styles.modalContent}>
                    <LabelMedium>{warningMessage}</LabelMedium>
                    <Strut size={16} />
                    {content}
                </View>
            }
            footer={footer}
        />
    );
};

const styles = StyleSheet.create({
    modalContent: {
        alignItems: "center",
        justifyContent: "flex-start",
    },
    footer: {
        width: "100%",
        flexDirection: "row",
        gap: spacing.small_12,
        justifyContent: "right",
    },
});
